import React from 'react';
import { ArrowRight, AlertTriangle } from 'lucide-react';
import { MIN_CART_VALUE } from '../../utils/cartConfig';

const CheckoutFooter = ({ 
  subtotal, 
  deliveryCharge, 
  packingCharge, 
  discountAmount = 0,
  appliedCoupon,
  grandTotal, 
  isOverallEligible, 
  sectionsEligibility,
  selectedAddress, 
  onContinue 
}) => {

  // Work out validation messages
  const getValidationErrors = () => {
    const errors = [];
    
    if (!selectedAddress) {
      errors.push("Please select a delivery address.");
    }
    
    if (subtotal < MIN_CART_VALUE) {
      errors.push(`Minimum cart value of ₹${MIN_CART_VALUE.toLocaleString('en-IN')} not reached.`);
    }

    if (sectionsEligibility && sectionsEligibility.length > 0) {
      const ineligibleSections = sectionsEligibility.filter(sec => !sec.eligible);
      if (ineligibleSections.length > 0) {
        errors.push("MOQ requirement not satisfied for some sections.");
      }
    }
    
    return errors;
  };

  const errors = getValidationErrors();
  const isValid = errors.length === 0;

  // Format delivery charge text
  const formatDelivery = (charge) => {
    return charge === 0 ? 'FREE' : `₹${charge.toLocaleString('en-IN')}`;
  };

  return (
    <>
      {/* DESKTOP FOOTER / SUMMARY CARD */}
      <div className="checkout-card checkout-footer-desktop">
        <h3 className="checkout-card-title">Order Summary</h3>
        
        <div className="checkout-summary-breakdown">
          <div className="summary-row">
            <span className="summary-label">Items Total</span>
            <span className="summary-value">₹{subtotal.toLocaleString('en-IN')}</span>
          </div>

          <div className="summary-row">
            <span className="summary-label">Packing Fee</span>
            <span className="summary-value">₹{packingCharge.toLocaleString('en-IN')}</span>
          </div>

          <div className="summary-row">
            <span className="summary-label">Shipping</span>
            <span className={`summary-value ${deliveryCharge === 0 ? 'free-shipping' : ''}`}>
              {formatDelivery(deliveryCharge)}
            </span>
          </div>

          {appliedCoupon && discountAmount > 0 && (
            <div className="summary-row">
              <span className="summary-label" style={{ color: '#10b981' }}>
                Coupon Amount ({appliedCoupon.code} - {appliedCoupon.discountType === 'FixedAmount' ? `₹${appliedCoupon.discountValue} OFF` : `${appliedCoupon.discountValue}% OFF`})
              </span>
              <span className="summary-value" style={{ color: '#10b981', fontWeight: '700' }}>
                - ₹{discountAmount.toLocaleString('en-IN')}
              </span>
            </div>
          )}

          <div className="summary-row grand-total-row">
            <span className="summary-label" style={{ fontWeight: '700', color: 'var(--color-text-primary)' }}>Grand Total</span>
            <span className="summary-value grand-total">₹{grandTotal.toLocaleString('en-IN')}</span>
          </div>
        </div>

        {errors.map((err, idx) => (
          <div key={idx} className="checkout-alert-box">
            <AlertTriangle size={16} />
            <span>{err}</span>
          </div>
        ))}

        <button
          type="button"
          onClick={onContinue}
          disabled={!isValid}
          className="btn-checkout-primary"
        >
          <span>Continue To Payment</span>
          <ArrowRight size={18} />
        </button>
      </div>

      {/* MOBILE LIGHTWEIGHT ORDER SUMMARY */}
      <div className="checkout-footer-mobile-summary">
        <div className="summary-row">
          <span className="summary-label">Items Total</span>
          <span className="summary-value">₹{subtotal.toLocaleString('en-IN')}</span>
        </div>
        <div className="summary-row">
          <span className="summary-label">Packing Fee</span>
          <span className="summary-value">₹{packingCharge.toLocaleString('en-IN')}</span>
        </div>
        <div className="summary-row">
          <span className="summary-label">Shipping</span>
          <span className={`summary-value ${deliveryCharge === 0 ? 'free-shipping-text' : ''}`}>
            {formatDelivery(deliveryCharge)}
          </span>
        </div>
        {appliedCoupon && discountAmount > 0 && (
          <div className="summary-row">
            <span className="summary-label" style={{ color: '#10b981' }}>
              Coupon Applied ({appliedCoupon.code} - {appliedCoupon.discountType === 'FixedAmount' ? `₹${appliedCoupon.discountValue} OFF` : `${appliedCoupon.discountValue}% OFF`})
            </span>
            <span className="summary-value" style={{ color: '#10b981', fontWeight: '700' }}>
              - ₹{discountAmount.toLocaleString('en-IN')}
            </span>
          </div>
        )}
        <div className="summary-row mobile-summary-grand-total">
          <span className="summary-label-grand">Grand Total</span>
          <span className="summary-value-grand">₹{grandTotal.toLocaleString('en-IN')}</span>
        </div>
      </div>

      {/* MOBILE FIXED WARNING BOX */}
      {errors.length > 0 && (
        <div className="mobile-warning-fixed">
          {errors[0]}
        </div>
      )}

      {/* MOBILE STICKY FOOTER */}
      <div className="checkout-footer-mobile">
        <div className="checkout-footer-mobile-info">
          <span className="mobile-grand-total-label">Grand Total</span>
          <span className="mobile-grand-total-val">₹{grandTotal.toLocaleString('en-IN')}</span>
        </div>

        <button
          type="button"
          onClick={onContinue}
          disabled={!isValid}
          className="btn-checkout-primary-mobile"
        >
          Continue &rarr;
        </button>
      </div>
    </>
  );
};

export default CheckoutFooter;
